import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Stack,
  Button,
  Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublicIcon from '@mui/icons-material/Public';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import { DataService } from 'src/services/data-service';
import LeaseCreateDialog from './LeaseCreateDialog';
import LeaseDetailsDialog from './LeaseDetailsDialog';
import CheckInDialog from './CheckInDialog';
import CheckOutDialog from './CheckOutDialog';

interface Unit {
  id: string;
  number: string;
  rooms: number;
  bathrooms: number;
  floor: number;
  areaM2: number;
  propertyId: string;
  property: any;
  unitFeeConfigs: any[];
  appliances: any[];
  active: boolean;
}

interface Tenant {
  id: string;
  fullName: string;
  phoneNumbers: string[];
  emails: string[];
  nationality: string;
  identificationType: string;
  files: any[];
  active: boolean;
}

interface Lease {
  id: string;
  createdDate: string;
  startDate: string;
  endDate: string;
  status: string;
  refNumber: string;
  leaseType: string;
  leaseTypeId: string;
  currency: string;
  currencyId: string;
  unit: Unit;
  tenant: Tenant;
  fees: any[];
  active: boolean;
  checkinDate?: string | null;
  checkoutDate?: string | null;
}

// Status mapping for string-based status
const LeaseStatus: Record<string, { label: string; color: 'warning' | 'success' | 'error' | 'default' | 'info' }> = {
  'Draft': { label: 'Draft', color: 'warning' },
  'Active': { label: 'Active', color: 'success' },
  'Expired': { label: 'Expired', color: 'error' },
  'Terminated': { label: 'Terminated', color: 'default' }
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8]
  }
}));

const LeaseHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  padding: theme.spacing(1.5),
  borderRadius: `${theme.spacing(1.5)} ${theme.spacing(1.5)} 0 0`
}));

const LeasesListing: React.FC = () => {
  const [leases, setLeases] = useState<Lease[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string | 'all'>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedLeaseId, setSelectedLeaseId] = useState<string>('');
  const [activatingLeaseId, setActivatingLeaseId] = useState<string | null>(null);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [selectedLeaseForCheckIn, setSelectedLeaseForCheckIn] = useState<Lease | null>(null);
  const [checkOutDialogOpen, setCheckOutDialogOpen] = useState(false);
  const [selectedLeaseForCheckOut, setSelectedLeaseForCheckOut] = useState<Lease | null>(null);

  useEffect(() => {
    fetchLeases();
  }, []);

  const fetchLeases = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DataService.get('api/Lease/all');

      if (response.ok) {
        const responseData = await response.json();
        console.log('Leases Response:', responseData);

        let leasesList: Lease[] = [];

        if (responseData.success && responseData.data) {
          leasesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          leasesList = responseData;
        }

        setLeases(leasesList);
      } else {
        throw new Error('Failed to fetch leases');
      }
    } catch (error) {
      console.error('Error fetching leases:', error);
      setError('Failed to load leases. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusInfo = (status: string) => {
    return LeaseStatus[status] || { label: status || 'Unknown', color: 'default' as const };
  };

  const handleActivateLease = async (leaseId: string) => {
    setActivatingLeaseId(leaseId);
    try {
      const response = await DataService.get(`api/Lease/activate/${leaseId}`);

      if (response.ok) {
        // Refresh the leases list
        await fetchLeases();
      } else {
        let errorMessage = `Failed to activate lease (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.title || JSON.stringify(errorData);
        } catch (e) {
          errorMessage = `API Error: ${response.status} ${response.statusText}`;
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Error activating lease:', err);
      setError('An error occurred while activating the lease');
    } finally {
      setActivatingLeaseId(null);
    }
  };

  const filteredLeases = filter === 'all'
    ? leases
    : leases.filter(lease => lease.status === filter);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 1, px: 2 }}>
      <Box>
        {/* Header */}
        <Box
          sx={{
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DescriptionIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold">
                Lease Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {leases.length} {leases.length === 1 ? 'lease' : 'leases'} total
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              size="small"
            >
              Create
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchLeases}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Filter Chips */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              label="All"
              onClick={() => setFilter('all')}
              color={filter === 'all' ? 'primary' : 'default'}
              variant={filter === 'all' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Draft"
              onClick={() => setFilter('Draft')}
              color={filter === 'Draft' ? 'warning' : 'default'}
              variant={filter === 'Draft' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Active"
              onClick={() => setFilter('Active')}
              color={filter === 'Active' ? 'success' : 'default'}
              variant={filter === 'Active' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Expired"
              onClick={() => setFilter('Expired')}
              color={filter === 'Expired' ? 'error' : 'default'}
              variant={filter === 'Expired' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Terminated"
              onClick={() => setFilter('Terminated')}
              color={filter === 'Terminated' ? 'default' : 'default'}
              variant={filter === 'Terminated' ? 'filled' : 'outlined'}
            />
          </Box>
        </Box>

        {/* Leases Grid */}
        {filteredLeases.length === 0 ? (
          <Paper
            sx={{
              p: 8,
              textAlign: 'center',
              bgcolor: 'grey.50'
            }}
          >
            <DescriptionIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
            <Typography variant="h5" color="text.secondary">
              No leases found
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {filteredLeases.map((lease) => {
              const statusInfo = getStatusInfo(lease.status);
              const daysRemaining = getDaysRemaining(lease.endDate);

              return (
                <Grid item xs={12} md={6} lg={4} key={lease.id}>
                  <StyledCard>
                    {/* Lease Header */}
                    <LeaseHeader>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold" fontSize="0.95rem">
                          Lease #{lease.id.substring(0, 8)}
                        </Typography>
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                          sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarTodayIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption" fontSize="0.7rem">
                          Created: {formatDate(lease.createdDate)}
                        </Typography>
                      </Box>
                    </LeaseHeader>

                    <CardContent sx={{ flexGrow: 1, p: 1, pt: 1.25 }}>
                      {/* Lease Period */}
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="primary" fontWeight="600" fontSize="0.75rem" display="block" mb={0.5}>
                          Lease Period
                        </Typography>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" fontSize="0.75rem">
                              <strong>Start:</strong> {formatDate(lease.startDate)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" fontSize="0.75rem">
                              <strong>End:</strong> {formatDate(lease.endDate)}
                            </Typography>
                          </Box>
                          {daysRemaining > 0 && (
                            <Chip
                              label={`${daysRemaining} days`}
                              size="small"
                              color={daysRemaining < 30 ? 'warning' : 'info'}
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                            />
                          )}
                        </Stack>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      {/* Lease Details */}
                      <Box sx={{ mb: 1 }}>
                        <Grid container spacing={0.5}>
                          <Grid item xs={6}>
                            <Box sx={{ bgcolor: 'grey.50', p: 0.75, borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                Type
                              </Typography>
                              <Typography variant="caption" fontWeight="600" fontSize="0.75rem" display="block">
                                {lease.leaseType.toUpperCase()}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{ bgcolor: 'grey.50', p: 0.75, borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                Currency
                              </Typography>
                              <Typography variant="caption" fontWeight="600" fontSize="0.75rem" display="block">
                                {lease.currency}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      {/* Tenant Information */}
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="primary" fontWeight="600" fontSize="0.75rem" display="block" mb={0.5}>
                          Tenant
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            <PersonIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="caption" fontWeight="600" fontSize="0.8rem" noWrap>
                              {lease.tenant.fullName}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <PublicIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" fontSize="0.65rem" noWrap>
                                {lease.tenant.nationality}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Stack spacing={0.3}>
                          {lease.tenant.emails.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 12 }} color="action" />
                              <Typography variant="caption" color="text.secondary" fontSize="0.7rem" noWrap>
                                {lease.tenant.emails[0]}
                              </Typography>
                            </Box>
                          )}
                          {lease.tenant.phoneNumbers.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 12 }} color="action" />
                              <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                                {lease.tenant.phoneNumbers[0]}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      {/* Unit Information */}
                      <Box>
                        <Typography variant="caption" color="primary" fontWeight="600" fontSize="0.75rem" display="block" mb={0.5}>
                          Unit
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                          <HomeIcon sx={{ fontSize: 14 }} color="primary" />
                          <Typography variant="caption" fontWeight="600" fontSize="0.8rem">
                            Unit {lease.unit.number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                            • Floor {lease.unit.floor}
                          </Typography>
                        </Box>
                        <Grid container spacing={0.5}>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 0.5, borderRadius: 1 }}>
                              <MeetingRoomIcon sx={{ fontSize: 16 }} color="primary" />
                              <Typography variant="caption" fontWeight="600" fontSize="0.8rem" display="block">
                                {lease.unit.rooms}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                Rooms
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 0.5, borderRadius: 1 }}>
                              <BathtubIcon sx={{ fontSize: 16 }} color="primary" />
                              <Typography variant="caption" fontWeight="600" fontSize="0.8rem" display="block">
                                {lease.unit.bathrooms}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                Baths
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 0.5, borderRadius: 1 }}>
                              <SquareFootIcon sx={{ fontSize: 16 }} color="primary" />
                              <Typography variant="caption" fontWeight="600" fontSize="0.8rem" display="block">
                                {lease.unit.areaM2}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                                m²
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Fees Badge */}
                      {lease.fees && lease.fees.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            icon={<AttachMoneyIcon sx={{ fontSize: 14 }} />}
                            label={`${lease.fees.length} Fee${lease.fees.length !== 1 ? 's' : ''}`}
                            color="success"
                            variant="outlined"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      )}

                      {/* Action Buttons */}
                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {/* Show Activate button only for Draft leases */}
                        {lease.status === 'Draft' && (
                          <Button
                            variant="contained"
                            color="success"
                            fullWidth
                            size="small"
                            startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleActivateLease(lease.id)}
                            disabled={activatingLeaseId === lease.id}
                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                          >
                            {activatingLeaseId === lease.id ? 'Activating...' : 'Activate'}
                          </Button>
                        )}
                        {/* Show Check In/Out buttons only for Active leases */}
                        {lease.status === 'Active' && !lease.checkinDate && (
                          <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="small"
                            startIcon={<LoginIcon sx={{ fontSize: 16 }} />}
                            onClick={() => {
                              setSelectedLeaseForCheckIn(lease);
                              setCheckInDialogOpen(true);
                            }}
                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                          >
                            Check In
                          </Button>
                        )}
                        {lease.status === 'Active' && lease.checkinDate && !lease.checkoutDate && (
                          <Button
                            variant="contained"
                            color="warning"
                            fullWidth
                            size="small"
                            startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
                            onClick={() => {
                              setSelectedLeaseForCheckOut(lease);
                              setCheckOutDialogOpen(true);
                            }}
                            sx={{ fontSize: '0.75rem', py: 0.5 }}
                          >
                            Check Out
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                          onClick={() => {
                            window.location.href = `/leases/${lease.id}`;
                          }}
                          sx={{ fontSize: '0.75rem', py: 0.5 }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* Create Lease Dialog */}
      <LeaseCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          fetchLeases();
          setCreateDialogOpen(false);
        }}
      />

      {/* Lease Details Dialog */}
      <LeaseDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        leaseId={selectedLeaseId}
        onLeaseUpdated={fetchLeases}
      />

      {/* Check In Dialog */}
      {selectedLeaseForCheckIn && (
        <CheckInDialog
          open={checkInDialogOpen}
          onClose={() => {
            setCheckInDialogOpen(false);
            setSelectedLeaseForCheckIn(null);
          }}
          lease={selectedLeaseForCheckIn}
          onSuccess={() => {
            setCheckInDialogOpen(false);
            setSelectedLeaseForCheckIn(null);
            fetchLeases(); // Refresh to show Check Out button
          }}
        />
      )}

      {/* Check Out Dialog */}
      {selectedLeaseForCheckOut && (
        <CheckOutDialog
          open={checkOutDialogOpen}
          onClose={() => {
            setCheckOutDialogOpen(false);
            setSelectedLeaseForCheckOut(null);
          }}
          lease={selectedLeaseForCheckOut}
          onSuccess={() => {
            setCheckOutDialogOpen(false);
            setSelectedLeaseForCheckOut(null);
            fetchLeases(); // Refresh lease list
          }}
        />
      )}
    </Container>
  );
};

export default LeasesListing;
